/**
 * Golem fork (FT-1403 phase 1 / FT-1397 items 1-3): THE NIGHT SIGNS
 * REGISTRY — one per-role entry for everything the night dress used to
 * decide by scattered role-id lists and guards.
 *
 * THE SHAPE. A role's night signs are three kinds of fact, and the registry
 * splits them by who can read them:
 *
 *   INK    the role's primary colour — style-readable, so it lives in the
 *          SCSS half of this registry, `golem/_roleInk.scss` (NightMark's
 *          palettes and Player.vue's glow maps both derive from that one
 *          map; see its header for the drift hazard it kills).
 *
 *   FLAGS  which pieces of dress furniture a role wants — JS-readable,
 *          here. These replace the hand-kept lists and role-id guards the
 *          audit named: NightMark's HAS_ART and its three told guards
 *          (character token / numeral badge / ledger), NightCall's chip and
 *          book branches, NightThread's line-token branch. A surface asks
 *          `signFlag(roleId, "wantsX")` and never names a role again.
 *
 *   RULES  per-role behavior keys that are neither art nor copy — today the
 *          one automation-suppression key (the Scarlet Woman's checklist
 *          row stands down while her automation covers it, FT-1314), read
 *          generically by the night roster instead of a hard-coded id.
 *
 * WHAT STAYS WHERE IT IS. TOLD_ROLES (the telling's data shape) keeps
 * living in golem/toldInfo — that is the information's grammar, not its
 * dress. TOLD_ART also stays authored there (the role-by-role landing
 * order is that file's story); this registry re-reads it so every surface
 * asks ONE module which dress exists. The 12-branch render itself stays in
 * NightMark.vue — the module split is a later card (the audit's item 4),
 * deliberately not this one.
 *
 * ── COPY AS DATA (FT-1403, then FT-1475 / FT-1486) ────────────────────────
 * The per-role night COPY stays authored where it always was — NIGHT_INFO's
 * `line`, CHOOSER_VERBS' invite/sealed, toldInfo's sentence builders — as
 * the DEFAULTS. Every read goes through `copyFor()` below, which consults
 * the ROLE'S OWN words first (`night.copy` on its library row — the same
 * keys: line, invite, sealed, telling, settled, seats, told — and, since
 * FT-1532, the face's per-state picked, sent, lineInvite, linePicked,
 * lineSealed, lineSent).
 *
 * FT-1486 (user, 2026-09-14): a character's night wording lives ONLY on the
 * character. The per-script layer (a custom script's `_meta.nightCopy`) is
 * no longer read: a script that still carries one keeps it in its saved
 * JSON, untouched, but it changes nothing on screen.
 *
 * A chooser's verb pair only stands when BOTH sentences resolve (the role's
 * words or the default): NightCall's whole verb grammar (chips yielding to
 * named lines, the padded Confirm) assumes a complete pair, so a role
 * authoring only `invite` keeps today's generic lines until it authors
 * `sealed` too.
 */
import Vue from "vue";
import { TOLD_ART, toldSentence } from "./toldInfo";
import { chooserVerbsFor, lineFor, splitLabelsFor } from "./nightInfo";
// FT-1404: the viewer's own "Show night signs" switch — a personal pref
// (golem/prefs' showNightSigns), mirrored below into one reactive gate.
import { prefsState, PREFS_EVENT } from "./prefs";

/**
 * The CHOOSING roles whose night-mark art has landed — grows one commit at
 * a time (FT-1384's role-by-role order: monk, poisoner, fortuneteller,
 * butler, imp, ravenkeeper). Everything else renders nothing and keeps the
 * app's standing purple idiom. (Moved whole from NightMark.vue, where it
 * was the template's HAS_ART.)
 */
const CHOOSER_ART = [
  "monk",
  "poisoner",
  "fortuneteller",
  "butler",
  "imp",
  "ravenkeeper",
];

/**
 * The per-role flag entries. A missing role, or a missing key, is FALSE —
 * the fallback is always "no extra furniture", which is exactly how an
 * undressed or unlisted role has always rendered.
 *
 *   wantsCharacterToken  the told character's icon hangs on the role's own
 *                        coin (the Librarian's bookmark token, FT-1400; the
 *                        Grandmother's spectacles token, FT-1402)
 *   wantsNumeralBadge    the told number persists as a badge on the coin
 *                        (the Empath's ring, the Chef's scorch — FT-1400)
 *                        and the number chip on the face wears the role's
 *                        own dress (NightCall's nf-num-* classes)
 *   wantsLedger          the nightly number history accumulates round the
 *                        coin (the Empath's FT-1401 ledger), and the face's
 *                        number chips speak its glyphs (heart for 0, the
 *                        blood run for a count)
 *   wantsLineToken       the told character's icon rides the thread's
 *                        midpoint (the Washerwoman's laundry line, the
 *                        Investigator's evidence string — FT-1400; the
 *                        Librarian's token hangs on her bookmark instead)
 *   wantsBook            the Librarian's book renders under her sentence on
 *                        the face, both beats (FT-1385)
 *   automationSuppress   the session automation flag that, while ON, stands
 *                        this role's checklist row down entirely (FT-1314's
 *                        user ruling — the automation does the row's whole
 *                        job, reveal included)
 */
const SIGNS = {
  washerwoman: { wantsLineToken: true },
  librarian: { wantsCharacterToken: true, wantsBook: true },
  investigator: { wantsLineToken: true },
  chef: { wantsNumeralBadge: true },
  empath: { wantsNumeralBadge: true, wantsLedger: true },
  grandmother: { wantsCharacterToken: true },
  // FT-1412: the Undertaker — the executed character's token on his own
  // coin (the told-character pattern's third member, no seat)
  undertaker: { wantsCharacterToken: true },
  scarletwoman: { automationSuppress: "autoScarletWoman" },
};

/** Has this CHOOSING role's mark art landed? (NightMark's old HAS_ART.) */
export function hasChooserArt(roleId) {
  return CHOOSER_ART.includes(roleId);
}

/** Has this TOLD role's two-beat dress landed? (toldInfo's TOLD_ART,
 *  re-read here so the surfaces ask one module.) */
export function hasToldArt(roleId) {
  return TOLD_ART.includes(roleId);
}

/** One dress-furniture flag, false for every role and key not listed. */
export function signFlag(roleId, flag) {
  const entry = SIGNS[roleId];
  return !!(entry && entry[flag]);
}

/** The session-automation flag that suppresses this role's checklist row
 *  while ON, or "" — the night roster reads this instead of a role id. */
export function automationSuppressFor(roleId) {
  const entry = SIGNS[roleId];
  return (entry && entry.automationSuppress) || "";
}

// ─── THE COPY LAYER (see COPY AS DATA in the header) ───────────────────────

/** The keys a role may author, and the one cap they all share — the same
 *  200 characters sanitizeAuthoredNight already allows a line. The cap is
 *  exported because the editor boxes enforce the same number in the markup
 *  rather than inventing a second one. */
export const COPY_KEYS = [
  "line",
  "invite",
  "sealed",
  "telling",
  "settled",
  // FT-1410: the SPLIT ROW's two labels on the storyteller's night sheet —
  // the word before the seat pickers and the word before the told control
  // ("Player:" … "Role:" on the first-night info rows). Two more keys through
  // the same door: the sanitizer loops COPY_KEYS, so a script, the Night lab
  // and the forge all carry them for free.
  "seats",
  "told",
  // FT-1532 (user): THE PLAYER'S FACE, PER STATE. The face carries two lines
  // — the purple ask and the white line under it — and both now take their
  // own words in each of the four states a chooser passes through
  // (Invitation, Picked, Confirmed, Sent). The ask already had two of its
  // four (`invite`, `sealed`); these are the other two. The white line had
  // ONE key (`line`, which the storyteller's sheet also reads) standing in
  // every state; it gains one per state, and `line` stays the sheet's.
  "picked",
  "sent",
  "lineInvite",
  "linePicked",
  "lineSealed",
  "lineSent",
];
export const COPY_MAX = 200;

/**
 * FT-1536 (user: "I should be able to make those empty. just let me delete
 * them"): THE KEYS A ROLE MAY LEAVE EMPTY ON PURPOSE — the face's two lines
 * in all four states and the base `line`.
 *
 *   key absent   use the default (as always)
 *   key ""       deliberately no words — the line shows nothing
 *
 * The other keys keep "empty = the default": a told sentence with no
 * template is COMPOSED (toldSentence), and the sheet's labels fall back to
 * the role's own verb, so an empty one of those has nothing to switch off.
 * Mirrored by shared/protocols/botc-role-night.ts BOTC_NIGHT_BLANKABLE_COPY_KEYS.
 */
export const BLANKABLE_KEYS = [
  "line",
  "invite",
  "picked",
  "sealed",
  "sent",
  "lineInvite",
  "linePicked",
  "lineSealed",
  "lineSent",
];

/**
 * FT-1532: WHICH KEYS SPEAK ON A CHOOSER'S FACE IN EACH STATE — the purple
 * ask (`ask`) and the white line (`line`). A chooser is a role whose player
 * picks something (a seat or a character); every other face keeps the one
 * `line` it always had.
 *
 *   state     ask       line          the white line when nobody wrote one
 *   invite    invite    lineInvite    the role's `line` (as before)
 *   picked    picked    linePicked    the role's `line` (as before)
 *   sealed    sealed    lineSealed    nothing — the picks' chips stand there
 *   sent      sent      lineSent      nothing — the picks' chips stand there
 *
 * (A receive-only role's Sent ask is still `sealed` — FT-1384's one wording
 * for both moments; NightCall decides that, not this table.)
 */
export const FACE_KEYS = {
  invite: { ask: "invite", line: "lineInvite" },
  picked: { ask: "picked", line: "linePicked" },
  sealed: { ask: "sealed", line: "lineSealed" },
  sent: { ask: "sent", line: "lineSent" },
};

/** FT-1532: the white-line keys whose unwritten words fall back to the
 *  role's `line`; the other two fall back to no words at all. */
const LINE_FROM_BASE = ["lineInvite", "linePicked"];

/** FT-1532: the ask's generic sentence once the storyteller has answered —
 *  the default of the `sent` key (FT-1291's line, unchanged). */
export const SENT_DEFAULT =
  "The storyteller has answered — your choice stands.";

/**
 * FT-1532: THE WHITE LINE for one face state's key. The role's own words for
 * that state first; else, in Invitation and Picked, `baseLine` (the role's
 * `line` as the face already resolved it — its own words or the shipped
 * reminder, so a line written before this card reads exactly as it did);
 * else, in Confirmed and Sent, nothing.
 */
export function faceLineFor(roleId, key, baseLine) {
  // FT-1536: a line left empty on purpose stays empty
  const own = ownCopyFor(roleId, key);
  if (own !== null) return own;
  return LINE_FROM_BASE.indexOf(key) !== -1 ? baseLine || "" : "";
}

/** One authored sentence, made safe: a string, markup stripped (tags out
 *  whole, stray angle brackets too), whitespace collapsed, capped. */
function cleanCopyString(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/[<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, COPY_MAX);
}

/** The app's one role-id alphabet (store/index.js's clean). */
function cleanRoleId(id) {
  return String(id)
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * ONE role's copy entry ({ key: sentence }) → the sanitized entry, or null
 * when nothing survives. Strict on purpose — a role's words are shared
 * content and the wire replays them: strings only, markup stripped, capped.
 * FT-1536: a BLANKABLE key whose string cleans to "" is kept as "" (left
 * empty on purpose); anything that is not a string (null, absent) is the
 * default and dropped, as is an empty value under any other key.
 */
export function sanitizeCopyEntry(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const clean = {};
  COPY_KEYS.forEach((key) => {
    if (typeof raw[key] !== "string") return;
    const v = cleanCopyString(raw[key]);
    if (v || BLANKABLE_KEYS.indexOf(key) !== -1) clean[key] = v;
  });
  return Object.keys(clean).length ? clean : null;
}

// ─── ROLE-LEVEL COPY (FT-1475, the Role Studio) ────────────────────────────
//
// User decision 2026-09-12: NIGHT WORDING MOVES ONTO THE ROLE. A role carries
// its own copy — `night.copy` on its library row, the same seven keys — and a
// sentence resolves in this order (FT-1486: the script's `_meta.nightCopy`
// that used to stand first is no longer read — wording lives on the role):
//
//   1. the ROLE'S OWN copy, from one of three maps —
//        draft   the Role Studio's UNSAVED words for the role on its bench,
//                standing in for the role's saved copy while the Studio's
//                preview runs (the editor clears it when the preview stops);
//        script  a CUSTOM role's copy, riding the script snapshot as
//                `golemNightCopy` — setCustomRoles rebuilds this map on
//                every landing, exactly like the authored night registry;
//        house   an OFFICIAL character's copy, laid on by the house overlay
//                (golem/houseRoles) from the officials' library rows;
//   2. the shipped defaults.
//
// All three hang off one observable — so `copyFor`'s read IS a dependency for
// every computed that resolves a night sentence — and every write REPLACES a
// whole map, so the swap is always the notification and a nested key is
// never set behind Vue's back.
const roleCopyGate = Vue.observable({
  draft: Object.create(null),
  script: Object.create(null),
  house: Object.create(null),
});

/** A map with one role's entry set (sanitized) or dropped (nothing left). */
function withRoleEntry(map, roleId, raw) {
  const id = roleId ? cleanRoleId(roleId) : "";
  if (!id) return map;
  const next = { ...map };
  const clean = sanitizeCopyEntry(raw);
  if (clean) next[id] = clean;
  else delete next[id];
  return next;
}

/** The house overlay's door: an official character's saved words. */
export function registerHouseCopy(roleId, raw) {
  roleCopyGate.house = withRoleEntry(roleCopyGate.house, roleId, raw);
}

/** An official character's standing house words, or null (the editor seeds
 *  its Words boxes from this). */
export function houseCopyFor(roleId) {
  return (roleId && roleCopyGate.house[roleId]) || null;
}

/** setCustomRoles' door: `[[roleId, rawCopy], …]` for the script's custom
 *  roles — the whole map rebuilt, so a role that left the script takes its
 *  words with it. */
export function registerScriptRoleCopy(entries) {
  let next = {};
  (Array.isArray(entries) ? entries : []).forEach(([id, raw]) => {
    next = withRoleEntry(next, id, raw);
  });
  roleCopyGate.script = next;
}

/** The Studio's live words for the ONE role on its bench. An empty entry is
 *  meaningful: it means "every box is empty", so the preview shows the
 *  shipped defaults rather than falling through to the saved copy. */
export function setDraftCopy(roleId, raw) {
  const id = roleId ? cleanRoleId(roleId) : "";
  if (!id) return;
  roleCopyGate.draft = { [id]: sanitizeCopyEntry(raw) || {} };
}

/** The Studio stopped previewing: its unsaved words stop standing in. */
export function clearDraftCopy() {
  if (Object.keys(roleCopyGate.draft).length) {
    roleCopyGate.draft = Object.create(null);
  }
}

/** FT-1536: the role's own words for one key — the draft where the Studio
 *  has one for this role, else the saved copy (custom or house) — as a
 *  string, "" when LEFT EMPTY on purpose, or null when the role says nothing
 *  (the default stands). */
export function ownCopyFor(roleId, key) {
  const draft = roleCopyGate.draft[roleId];
  const own =
    draft || roleCopyGate.script[roleId] || roleCopyGate.house[roleId];
  const v = own && own[key];
  if (typeof v !== "string") return null;
  return v || BLANKABLE_KEYS.indexOf(key) !== -1 ? v : null;
}

/** FT-1536: has the role left this line empty on purpose? */
export function copyIsBlank(roleId, key) {
  return ownCopyFor(roleId, key) === "";
}

/** The role's own sentence for one key — the draft where the Studio has one
 *  for this role, else the saved copy (custom or house) — or "" (both "no
 *  words" and "left empty"; ownCopyFor tells those apart). */
export function roleCopyFor(roleId, key) {
  return ownCopyFor(roleId, key) || "";
}

/**
 * THE accessor — one role's copy for one key: the ROLE'S OWN copy first
 * (FT-1475), the shipped default last.
 * `ctx.isFirstNight` feeds the `line` default (a shipped line may be split
 * { first, other }); the told sentence keys have no raw default template
 * (toldSentence composes those) so they answer "" and `toldSentenceFor`
 * below owns the fallback.
 */
export function copyFor(roleId, key, ctx) {
  // FT-1486: no script `_meta.nightCopy` here — wording lives on the role.
  // FT-1536: a line left empty on purpose answers "" — the default does NOT
  // stand in (callers that need SOME text, the storyteller's sheet row, keep
  // their own `|| reminder` fallback on top)
  const own = ownCopyFor(roleId, key);
  if (own !== null) return own;
  return defaultCopyFor(roleId, key, ctx);
}

/**
 * THE OVERRIDE ALONE — the authored sentence for one role and key, or "".
 *
 * FT-1410: for the callers whose SHIPPED default is not this layer's to
 * decide. The night sheet's single-label row is the case: a row with one half
 * keeps its own verb ("Kills:", "Poisons:", "Dies:") — the user's ruling — so
 * the sheet asks for an override and falls back to the role's authored label
 * itself. Nothing changes for a script that says nothing, which is the whole
 * point of asking this way rather than moving the default in here.
 */
export function overrideFor(roleId, key) {
  // FT-1475: a role's own words are the override of the shipped label
  // (FT-1486: the only one — a script no longer carries wording)
  return roleCopyFor(roleId, key);
}

/**
 * THE SHIPPED DEFAULT for one role and key, overrides ignored — what an
 * empty override box is standing in for. Split out of `copyFor` (rather
 * than restated anywhere) so the FT-1403 phase 2 editor can show the real
 * default as its placeholder without a second copy of a single string.
 */
export function defaultCopyFor(roleId, key, ctx) {
  switch (key) {
    case "line":
      return lineFor(roleId, ctx && ctx.isFirstNight);
    case "invite":
    case "sealed": {
      const verbs = chooserVerbsFor(roleId);
      return (verbs && verbs[key]) || "";
    }
    // FT-1410: the split row's two labels — STRUCTURAL, off the field list
    // (nightInfo's splitLabelsFor), never a role-name list here.
    case "seats":
    case "told":
      return splitLabelsFor(roleId)[key];
    // FT-1532: the face's per-state words. The white line's Invitation and
    // Picked words fall back to the role's `line` (faceLineFor owns that —
    // it is the role's own copy, not a shipped default); `picked` has no
    // fixed sentence (NightCall's hint depends on the slots and the verbs).
    case "sent":
      return SENT_DEFAULT;
    default:
      return "";
  }
}

/** A chooser's verb templates through the override layer, or null — null
 *  keeps the generic lines exactly as chooserVerbsFor's null always has.
 *  FT-1497: the pair stands once EITHER sentence resolves; a half with no
 *  words at all comes back "" and NightCall speaks its generic line there
 *  (so a role authoring only its invite shows that invite — the header's
 *  complete-pair rule used to hide it until `sealed` was written too). */
export function verbsFor(roleId) {
  const invite = copyFor(roleId, "invite");
  const sealed = copyFor(roleId, "sealed");
  return invite || sealed ? { invite, sealed } : null;
}

// ─── THE NIGHT SIGNS TOGGLE (FT-1404, user-decided) ────────────────────────
// One gate, consulted by the DRESS layer alone: NightMark's art, the
// night threads, the coin glows (Player.vue's night-role-/told- classes),
// the Empath's ledger. Everything FUNCTIONAL ignores it — picks, staging,
// Confirm, the verb copy, the answer chips, the generic purple pick ring —
// so signs OFF plays exactly like an undressed role always has. (The
// FT-1396 evil chair badges are team knowledge, not night signs, and never
// consult this.)
//
// prefsState is a plain module object (its own header's rule), so the gate
// keeps a Vue.observable mirror refreshed on PREFS_EVENT — a computed that
// calls enabled() re-runs the moment the toggle flips, with no per-file
// readPrefs plumbing.
const signsGate = Vue.observable({
  on: prefsState.showNightSigns !== false,
});
try {
  window.addEventListener(PREFS_EVENT, () => {
    signsGate.on = prefsState.showNightSigns !== false;
  });
} catch (e) {
  // no window (tests): the boot value stands
}

/** Does this viewer's dress layer render the night signs? */
export function enabled() {
  return signsGate.on;
}

/** A telling's sentence through the override layer — the script's own
 *  template ({names} / {number} / {character}) when one is authored for
 *  this beat, toldInfo's composed sentence otherwise. */
export function toldSentenceFor(told, names, settled) {
  const tpl = told && copyFor(told.roleId, settled ? "settled" : "telling");
  if (tpl) {
    const number =
      told.number === null || told.number === undefined || told.number === ""
        ? ""
        : String(told.number);
    return tpl
      .replace(/\{names\}/g, names.filter(Boolean).join(" & "))
      .replace(/\{number\}/g, number)
      .replace(/\{character\}/g, told.characterName || "");
  }
  return toldSentence(told, names, settled);
}
